import React from "react";
import { Check, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { STEP_META } from "./constants";

export function WizardProgress({
  currentIndex, doneKeys, onJump,
}: {
  currentIndex: number;
  doneKeys: Record<string, boolean>;
  onJump: (index: number) => void;
}) {
  const total = STEP_META.length;
  const completed = STEP_META.filter((s) => doneKeys[s.key]).length;
  const pct = Math.round((completed / (total - 1)) * 100);

  return (
    <>
      {/* Mobile: compact progress */}
      <div className="lg:hidden" data-testid="wizard-progress-mobile">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-medium text-slate-900">Step {currentIndex + 1} of {total}</span>
          <span className="text-slate-500">{completed} complete</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-blue-600 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Desktop: vertical rail */}
      <nav className="hidden lg:block" data-testid="wizard-progress">
        <ol className="space-y-1">
          {STEP_META.map((step, i) => {
            const Icon: LucideIcon = step.icon;
            const done = !!doneKeys[step.key];
            const active = i === currentIndex;
            const reachable = i <= currentIndex || done;
            return (
              <li key={step.key}>
                <button
                  type="button"
                  disabled={!reachable}
                  onClick={() => reachable && onJump(i)}
                  data-testid={`wizard-step-${step.key}`}
                  className={cn(
                    "group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                    active ? "bg-blue-50" : "hover:bg-slate-50",
                    !reachable && "cursor-default opacity-50 hover:bg-transparent",
                  )}
                >
                  <span className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-colors",
                    done ? "border-emerald-200 bg-emerald-50 text-emerald-600"
                      : active ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-200 bg-white text-slate-400",
                  )}>
                    {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </span>
                  <span className="min-w-0">
                    <span className={cn("block truncate text-sm font-medium", active ? "text-blue-700" : "text-slate-900")}>
                      {step.title}
                    </span>
                    <span className="block truncate text-xs text-slate-400">{step.subtitle}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
